'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { ArrowRight, Sun, Moon, UploadCloud, Wrench, Shield } from 'lucide-react'
import Image from 'next/image'
import { cn } from '@/lib/utils'
import { useAuth } from '@/context/AuthContext'
import { useTheme } from '@/context/ThemeContext'
import { Button } from '@/components/ui/button'
import { getAllPlugins } from '@/plugins'
import { ComingSoonBadge } from '@/components/ComingSoonBadge'
import { UserAvatarMenu } from '@/components/UserAvatarMenu'

export function AppSelector() {
  const router = useRouter()
  const { username, isAdmin } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const [mounted, setMounted] = useState(false)
  const [dropActive, setDropActive] = useState(false)
  const [droppedFileName, setDroppedFileName] = useState<string | null>(null)
  const [tappedUpgrading, setTappedUpgrading] = useState<string | null>(null)

  const plugins = getAllPlugins()

  useEffect(() => {
    setMounted(true)
  }, [])

  // Get display name (use username if available, otherwise 'Guest')
  const displayName = username && username !== 'guest' ? username : 'Guest'

  const handleAppClick = (route?: string) => {
    if (!route) return
    router.push(route)
  }

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    if (!dropActive) {
      setDropActive(true)
    }
  }

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    if (dropActive) {
      setDropActive(false)
    }
  }

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setDropActive(false)

    const files = Array.from(event.dataTransfer.files || [])
    if (files.length > 0) {
      const file = files[0]
      setDroppedFileName(file.name)
      // placeholder for future upload logic
      console.info('[Plugin Upload] Received file:', file.name)
    }
  }

  return (
    <div className="min-h-screen w-full bg-background overflow-hidden">
      {/* Top-right controls: avatar + theme toggle */}
      <div className="absolute top-6 right-6 z-20 flex items-center gap-2">
        <UserAvatarMenu
          scopeLabel="Platform"
          onAccountClick={() => router.push('/account')}
        />
        <Button
          size="icon"
          variant="outline"
          className="shrink-0"
          onClick={toggleTheme}
          aria-label="Toggle theme"
        >
          {theme === 'dark' ? (
            <Sun size={18} className="transition-all duration-300" />
          ) : (
            <Moon size={18} className="transition-all duration-300" />
          )}
        </Button>
      </div>

      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 py-20">
        {/* Greeting and instruction */}
        <div
          className={cn(
            "mb-14 flex flex-col items-center gap-1 transition-opacity duration-500",
            mounted ? "opacity-100" : "opacity-0"
          )}
        >
          <h1 className="text-display">Hello, {displayName}</h1>
          <p className="text-sm text-muted-foreground">Choose an app to get started</p>
        </div>

        {/* Plugin grid (includes upload entry) */}
        <div className="grid w-full max-w-5xl grid-cols-1 gap-6 px-4 md:grid-cols-2 md:gap-8 xl:grid-cols-3">
          {plugins.map((plugin, index) => {
            const Icon = plugin.icon
            const isUpgrading = plugin.availability === 'upgrading'
            const isClickable = Boolean(plugin.route && plugin.availability === 'enabled')
            const isGradeAI = plugin.id === "gradeai"
            const gradeAILogoSrc = theme === "dark" ? "/images/gradeai-logo-white-bg.png" : "/images/gradeai-logo.png"
            const showTappedOverlay = tappedUpgrading === plugin.id
            const ctaText = isClickable ? 'Open' : isUpgrading ? 'Upgrading' : 'Coming soon'
            const badge = plugin.badge ?? (isUpgrading ? null : (!isClickable ? <ComingSoonBadge /> : null))

            return (
              <div
                key={plugin.id}
                className={cn(
                  "group relative transition-opacity duration-500",
                  mounted ? "opacity-100" : "opacity-0",
                  isClickable ? "cursor-pointer" : isUpgrading ? "cursor-pointer" : "cursor-not-allowed opacity-60"
                )}
                style={{ transitionDelay: `${index * 60}ms` }}
                onMouseLeave={() => { if (showTappedOverlay) setTappedUpgrading(null) }}
                onClick={() => {
                  if (isUpgrading) {
                    setTappedUpgrading(plugin.id)
                    setTimeout(() => setTappedUpgrading(null), 2500)
                  } else {
                    handleAppClick(plugin.route)
                  }
                }}
              >
                {/* Card body */}
                <div
                  className={cn(
                    "relative flex h-full min-h-[220px] flex-col overflow-hidden rounded-lg border border-border bg-card p-6 shadow-subtle transition-shadow duration-200",
                    !isUpgrading && "group-hover:border-ring/40 group-hover:shadow-popover"
                  )}
                >
                  {badge && <div className="absolute right-5 top-5">{badge}</div>}

                  {/* Icon area */}
                  <div
                    className={cn(
                      "mb-5 flex size-14 items-center justify-center rounded-xl",
                      isGradeAI ? "bg-transparent" : "bg-secondary",
                      isUpgrading && "opacity-50"
                    )}
                  >
                    {isGradeAI ? (
                      <Image
                        src={gradeAILogoSrc}
                        alt="GradeAI logo"
                        width={56}
                        height={56}
                        className="size-14 rounded-xl object-cover"
                      />
                    ) : (
                      <Icon className="size-7 text-secondary-foreground" />
                    )}
                  </div>

                  <h2 className={cn("text-title mb-1.5", isUpgrading && "text-muted-foreground")}>
                    {plugin.name}
                  </h2>
                  <p className="mb-4 text-[13px] leading-5 text-muted-foreground">
                    {plugin.description}
                  </p>

                  {/* Status indicator */}
                  <div
                    className={cn(
                      "mt-auto flex items-center gap-1.5 text-sm font-medium",
                      isClickable ? "text-primary" : "text-muted-foreground"
                    )}
                  >
                    {isUpgrading && <Wrench className="size-4" />}
                    <span>{ctaText}</span>
                    {isClickable && (
                      <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                    )}
                  </div>

                  {isUpgrading && (
                    <div
                      className={cn(
                        "absolute inset-0 z-10 flex flex-col items-center justify-center gap-1 bg-background/90 transition-opacity duration-200",
                        showTappedOverlay ? "opacity-100" : "pointer-events-none opacity-0"
                      )}
                    >
                      <Wrench className="mb-2 size-6 text-muted-foreground" />
                      <p className="font-medium">Under construction</p>
                      <p className="text-sm text-muted-foreground">Check back later</p>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
          {/* Plugin upload card */}
          <div
            key="plugin-upload"
            className={cn(
              "relative flex h-full min-h-[220px] flex-col items-center justify-center gap-4 rounded-lg border border-dashed px-6 py-8 transition-colors duration-200",
              dropActive ? "border-ring/60 bg-accent" : "border-border"
            )}
            onDragOver={handleDragOver}
            onDragEnter={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div
              className={cn(
                "flex size-16 items-center justify-center rounded-lg transition-colors",
                dropActive ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
              )}
            >
              <UploadCloud className="size-7" />
            </div>
            <div className="px-4 text-center">
              <p className="text-lg font-semibold">Install Plugin Package</p>
              <p className="mt-2 text-sm text-muted-foreground">
                Drag & drop a plugin bundle (.zip) here, or click to browse. The installer will unpack and register the
                plugin automatically.
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3 text-xs text-muted-foreground">
              <ComingSoonBadge />
              {droppedFileName && (
                <span className="rounded-full bg-muted px-3 py-1 font-medium text-foreground/80">
                  Last uploaded: {droppedFileName}
                </span>
              )}
            </div>
            <input
              type="file"
              accept=".zip,.tar,.tar.gz"
              className="absolute inset-0 cursor-pointer opacity-0"
              onChange={(event) => {
                const file = event.target.files?.[0]
                if (file) {
                  setDroppedFileName(file.name)
                  console.info('[Plugin Upload] Selected file:', file.name)
                }
              }}
            />
          </div>
        </div>

        {/* Admin link */}
        {isAdmin && (
          <div
            className={cn(
              "mt-10 transition-all duration-1000",
              mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            )}
            style={{ transitionDelay: '500ms' }}
          >
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => router.push('/admin')}
            >
              <Shield className="h-4 w-4" />
              Admin Panel
            </Button>
          </div>
        )}

        {/* Bottom hint */}
        <div
          className={cn(
            "mt-16 text-center text-sm text-muted-foreground transition-all duration-1000",
            mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          )}
          style={{ transitionDelay: '600ms' }}
        >
          Select an app to get started
        </div>
      </div>
    </div>
  )
}

